import React, { useEffect, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { IconX } from '@tabler/icons-react';
import { Announcement } from '../../../../api/userAnnouncements';
import { trackComponentInteraction } from '../../../../utils/analytics';
import classes from './AnnouncementModal.module.css';

let isDomPurifyConfigured = false;

const ensureDomPurifyConfigured = () => {
    if (isDomPurifyConfigured) {
        return;
    }

    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
        if ('setAttribute' in node && 'tagName' in node) {
            const element = node as Element;
            if (element.tagName.toLowerCase() !== 'a' || !element.hasAttribute('href')) {
                return;
            }

            element.setAttribute('target', '_blank');

            const existingRel = element.getAttribute('rel') ?? '';
            const relTokens = new Set(existingRel.split(/\s+/).filter(Boolean));
            relTokens.add('noopener');
            relTokens.add('noreferrer');
            element.setAttribute('rel', Array.from(relTokens).join(' '));
        }
    });

    isDomPurifyConfigured = true;
};

interface AnnouncementProps {
    announcement: Announcement;
    onClose: () => void;
}

export const AnnouncementModal: React.FC<AnnouncementProps> = ({ announcement, onClose }: AnnouncementProps) => {
    ensureDomPurifyConfigured();

    const sanitizedContent = useMemo(
        () => DOMPurify.sanitize(announcement.content ?? ''),
        [announcement.content]
    );

    useEffect(() => {
        trackComponentInteraction(
            'AnnouncementModal',
            announcement.title,
            'modal',
            'view',
            announcement.announcementID
        );
    }, [announcement.announcementID, announcement.title]);

    const handleClose = () => {
        trackComponentInteraction(
            'AnnouncementModal',
            announcement.title,
            'modal',
            'dismiss',
            announcement.announcementID
        );
        onClose();
    };

    return (
        <div className={classes.modal} tabIndex={-1} data-autofocus>
            <div className={classes.header}>
                <h2 id="announcement-title" className={classes.title}>
                    {announcement.title}
                </h2>
                <button
                    type="button"
                    className={classes.closeButton}
                    onClick={handleClose}
                    aria-label="Close announcement"
                >
                    <IconX size={22} stroke={1.6} />
                </button>
            </div>
            <div
                className={classes.content}
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />
        </div>
    );
};
